import { useCallback } from 'react';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { isNil } from 'lodash';
import {
  ApiService,
  CompanyDailyNavConfigUpdate,
  DailyNavConfigApplyRequest,
  FirmDailyNavCompanyConfigBulkUpdate,
  FirmDailyNavConfigUpdate,
} from 'api';
import {
  DAILY_NAV_RECORDS_BY_FIRM_QUERY_KEY,
} from 'common/constants/services/firms';
import { useResponse } from 'services/hooks/useResponse';

export const FIRM_DAILY_NAV_CONFIG_QUERY_KEY = 'FIRM_DAILY_NAV_CONFIG';
export const FIRM_DAILY_NAV_CONFIG_HISTORY_QUERY_KEY = 'FIRM_DAILY_NAV_CONFIG_HISTORY';
export const COMPANY_DAILY_NAV_CONFIG_QUERY_KEY = 'COMPANY_DAILY_NAV_CONFIG';
export const FIRM_DAILY_NAV_COMPANY_CONFIGS_QUERY_KEY = 'FIRM_DAILY_NAV_COMPANY_CONFIGS';
export const COMPANY_DAILY_NAV_CONFIG_HISTORY_QUERY_KEY = 'COMPANY_DAILY_NAV_CONFIG_HISTORY';
export const COMPANY_DAILY_NAV_GPC_APPROACHES_QUERY_KEY = 'COMPANY_DAILY_NAV_GPC_APPROACHES';
export const DAILY_NAV_CONFIG_APPLY_QUERY_KEY = 'DAILY_NAV_CONFIG_APPLY';

// DRF page-number pagination: pages are always sequential, so the next page
// number is just how many pages have already loaded, plus one.
const getNextHistoryPageParam = <T extends { next?: string | null }>(lastPage: T, allPages: T[]) =>
  lastPage.next ? allPages.length + 1 : undefined;

export const useGetFirmDailyNavConfig = (firmId?: number | null) => {
  const { data, isFetching, isLoading, refetch } = useQuery({
    enabled: !isNil(firmId),
    queryFn: () => ApiService.apiFirmsDailyNavConfigList(String(firmId)),
    queryKey: [FIRM_DAILY_NAV_CONFIG_QUERY_KEY, firmId],
  });

  return { data, isFetching, isLoading, refetch };
};

export const useGetFirmDailyNavConfigHistory = (firmId?: number | null) => {
  const { data, isFetching, isLoading, fetchNextPage, hasNextPage, isFetchingNextPage } = useInfiniteQuery({
    enabled: !isNil(firmId),
    queryKey: [FIRM_DAILY_NAV_CONFIG_HISTORY_QUERY_KEY, firmId],
    queryFn: ({ pageParam }) => ApiService.apiFirmsDailyNavConfigHistoryList(String(firmId), pageParam),
    initialPageParam: 1,
    getNextPageParam: getNextHistoryPageParam,
  });

  return {
    history: data?.pages.flatMap(page => page.results),
    isFetching,
    isLoading,
    loadMoreHistory: fetchNextPage,
    hasMoreHistory: Boolean(hasNextPage),
    isLoadingMoreHistory: isFetchingNextPage,
  };
};

export const useUpdateFirmDailyNavConfig = () => {
  const queryClient = useQueryClient();
  const { processErrorResponse } = useResponse();

  const { mutateAsync, isPending } = useMutation({
    mutationFn: ({ firmId, data }: { firmId: number; data: FirmDailyNavConfigUpdate }) =>
      ApiService.apiFirmsDailyNavConfigUpdate(String(firmId), data),
  });

  const updateFirmDailyNavConfig = useCallback(
    async (firmId: number, data: FirmDailyNavConfigUpdate) => {
      try {
        const updatedConfig = await mutateAsync({ firmId, data });
        await queryClient.invalidateQueries({ queryKey: [FIRM_DAILY_NAV_CONFIG_QUERY_KEY, firmId] });
        await queryClient.invalidateQueries({ queryKey: [FIRM_DAILY_NAV_CONFIG_HISTORY_QUERY_KEY, firmId] });
        // A firm template change cascades a new config version to every company
        await queryClient.invalidateQueries({ queryKey: [COMPANY_DAILY_NAV_CONFIG_QUERY_KEY] });
        await queryClient.invalidateQueries({ queryKey: [COMPANY_DAILY_NAV_CONFIG_HISTORY_QUERY_KEY] });
        await queryClient.invalidateQueries({ queryKey: [FIRM_DAILY_NAV_COMPANY_CONFIGS_QUERY_KEY] });
        // Every preview is derived from the open version, so the save invalidates all of
        // them: the counts compare each record against it, and the company-scoped
        // blocked_reason turns on whether it still anchors to the date's firm version.
        // Unscoped because the cascade reaches every company. Without this the Apply
        // button re-enables (isDirty clears on save) against a pre-save preview.
        await queryClient.invalidateQueries({ queryKey: [DAILY_NAV_CONFIG_APPLY_QUERY_KEY] });
        return updatedConfig;
      } catch (error) {
        processErrorResponse({
          error,
          defaultErrorMessage: 'An error occurred while saving the Daily NAV settings',
          action: 'save the Daily NAV settings',
        });
        return null;
      }
    },
    [mutateAsync, queryClient, processErrorResponse]
  );

  return { updateFirmDailyNavConfig, isPending };
};

export const useGetCompanyDailyNavConfig = (firmId?: number | null, companyId?: number | null) => {
  const { data, isFetching, isLoading, refetch } = useQuery({
    enabled: !isNil(firmId) && !isNil(companyId),
    queryFn: () => ApiService.apiFirmsDailyNavCompaniesConfigList(String(firmId), String(companyId)),
    queryKey: [COMPANY_DAILY_NAV_CONFIG_QUERY_KEY, firmId, companyId],
  });

  return { data, isFetching, isLoading, refetch };
};

export const useGetCompanyDailyNavConfigHistory = (firmId?: number | null, companyId?: number | null) => {
  const { data, isFetching, isLoading, fetchNextPage, hasNextPage, isFetchingNextPage } = useInfiniteQuery({
    enabled: !isNil(firmId) && !isNil(companyId),
    queryKey: [COMPANY_DAILY_NAV_CONFIG_HISTORY_QUERY_KEY, firmId, companyId],
    queryFn: ({ pageParam }) =>
      ApiService.apiFirmsDailyNavCompaniesConfigHistoryList(String(firmId), String(companyId), pageParam),
    initialPageParam: 1,
    getNextPageParam: getNextHistoryPageParam,
  });

  return {
    history: data?.pages.flatMap(page => page.results),
    isFetching,
    isLoading,
    loadMoreHistory: fetchNextPage,
    hasMoreHistory: Boolean(hasNextPage),
    isLoadingMoreHistory: isFetchingNextPage,
  };
};

// The company's GPC approaches and their comps, offered up as the source list for the
// "copy from GPC" Daily NAV comps watchlist picker. This is a one-time copy, so no
// invalidation coupling to the config mutation is needed.
export const useGetCompanyGpcApproaches = (companyMeasurementDateId?: number | null) => {
  const { data, isLoading } = useQuery({
    enabled: !isNil(companyMeasurementDateId),
    queryFn: () => ApiService.apiDailyNavCompanyMeasurementDatesGpcApproachesList(String(companyMeasurementDateId)),
    queryKey: [COMPANY_DAILY_NAV_GPC_APPROACHES_QUERY_KEY, companyMeasurementDateId],
  });

  return { approaches: data, isLoading };
};

export const useUpdateCompanyDailyNavConfig = () => {
  const queryClient = useQueryClient();
  const { processErrorResponse } = useResponse();

  const { mutateAsync, isPending } = useMutation({
    mutationFn: ({
      firmId,
      companyId,
      data,
    }: {
      firmId: number;
      companyId: number;
      data: CompanyDailyNavConfigUpdate;
    }) => ApiService.apiFirmsDailyNavCompaniesConfigUpdate(String(firmId), String(companyId), data),
  });

  const updateCompanyDailyNavConfig = useCallback(
    async (firmId: number, companyId: number, data: CompanyDailyNavConfigUpdate) => {
      try {
        const updatedConfig = await mutateAsync({ firmId, companyId, data });
        await queryClient.invalidateQueries({ queryKey: [COMPANY_DAILY_NAV_CONFIG_QUERY_KEY, firmId, companyId] });
        await queryClient.invalidateQueries({
          queryKey: [COMPANY_DAILY_NAV_CONFIG_HISTORY_QUERY_KEY, firmId, companyId],
        });
        // Unscoped, unlike the two above: this company's records count toward the
        // FIRM-wide preview's totals too, so narrowing to [firmId, companyId] would
        // leave the Daily NAV Settings preview quoting pre-save counts.
        await queryClient.invalidateQueries({ queryKey: [DAILY_NAV_CONFIG_APPLY_QUERY_KEY] });
        await queryClient.invalidateQueries({ queryKey: [FIRM_DAILY_NAV_COMPANY_CONFIGS_QUERY_KEY] });
        return updatedConfig;
      } catch (error) {
        processErrorResponse({
          error,
          defaultErrorMessage: 'An error occurred while saving the Daily NAV Settings configuration',
          action: 'save the Daily NAV Settings configuration',
        });
        return null;
      }
    },
    [mutateAsync, queryClient, processErrorResponse]
  );

  return { updateCompanyDailyNavConfig, isPending };
};

export const useGetFirmDailyNavCompanyConfigs = (firmId?: number | null) => {
  const { data, isFetching, isLoading, refetch } = useQuery({
    enabled: !isNil(firmId),
    queryFn: () => ApiService.apiFirmsDailyNavCompanyConfigsList(String(firmId)),
    queryKey: [FIRM_DAILY_NAV_COMPANY_CONFIGS_QUERY_KEY, firmId],
  });

  return { data, isFetching, isLoading, refetch };
};

export const useUpdateFirmDailyNavCompanyConfigs = () => {
  const queryClient = useQueryClient();
  const { processErrorResponse } = useResponse();

  const { mutateAsync, isPending } = useMutation({
    mutationFn: ({ firmId, data }: { firmId: number; data: FirmDailyNavCompanyConfigBulkUpdate }) =>
      ApiService.apiFirmsDailyNavCompanyConfigsUpdate(String(firmId), data),
  });

  const updateFirmDailyNavCompanyConfigs = useCallback(
    async (firmId: number, data: FirmDailyNavCompanyConfigBulkUpdate) => {
      try {
        const updated = await mutateAsync({ firmId, data });
        await queryClient.invalidateQueries({ queryKey: [FIRM_DAILY_NAV_COMPANY_CONFIGS_QUERY_KEY, firmId] });
        await queryClient.invalidateQueries({ queryKey: [COMPANY_DAILY_NAV_CONFIG_QUERY_KEY] });
        await queryClient.invalidateQueries({ queryKey: [COMPANY_DAILY_NAV_CONFIG_HISTORY_QUERY_KEY] });
        await queryClient.invalidateQueries({ queryKey: [DAILY_NAV_CONFIG_APPLY_QUERY_KEY] });
        return updated;
      } catch (error) {
        processErrorResponse({
          error,
          defaultErrorMessage: 'An error occurred while saving Daily NAV company configuration',
          action: 'save Daily NAV company configuration',
        });
        return null;
      }
    },
    [mutateAsync, queryClient, processErrorResponse]
  );

  return { updateFirmDailyNavCompanyConfigs, isPending };
};

// Previews which in-progress day(s) can be re-pointed onto the open config, and why not.
// firmId + companyId both key the cache so firm-wide and company-scoped previews don't collide.
export const useGetDailyNavConfigApplyPreview = (firmId?: number | null, companyId?: number | null) => {
  const { data, isFetching, isLoading, refetch } = useQuery({
    enabled: !isNil(firmId),
    // 2nd positional arg is a meaningless inherited `page` param; must stay undefined so
    // companyId lands in the 3rd (company_id) slot instead of being sent as page.
    queryFn: () => ApiService.apiFirmsDailyNavConfigApplyList(String(firmId), undefined, companyId ?? undefined),
    queryKey: [DAILY_NAV_CONFIG_APPLY_QUERY_KEY, firmId, companyId],
  });

  return { data, isFetching, isLoading, refetch };
};

export const useApplyDailyNavConfig = () => {
  const queryClient = useQueryClient();
  const { processErrorResponse } = useResponse();

  const { mutateAsync, isPending } = useMutation({
    mutationFn: ({ firmId, data }: { firmId: number; data: DailyNavConfigApplyRequest }) =>
      ApiService.apiFirmsDailyNavConfigApplyCreate(String(firmId), data),
  });

  // expectedFirmConfigVersionId is the version the UI displayed as current; a stale value
  // gets refused (400) rather than silently applying a version the operator never reviewed.
  const applyDailyNavConfig = useCallback(
    async (firmId: number, expectedFirmConfigVersionId: number, companyId?: number) => {
      try {
        const response = await mutateAsync({
          firmId,
          data: { expected_firm_config_version_id: expectedFirmConfigVersionId, company_id: companyId },
        });
        await queryClient.invalidateQueries({ queryKey: [DAILY_NAV_CONFIG_APPLY_QUERY_KEY] });
        // Deltas/thresholds are derived server-side from a record's config version, so
        // re-anchoring changes what this cached query should show. It carries the
        // Previous Valuation band too, so one invalidation now covers both.
        await queryClient.invalidateQueries({ queryKey: [DAILY_NAV_RECORDS_BY_FIRM_QUERY_KEY] });
        await queryClient.invalidateQueries({ queryKey: [FIRM_DAILY_NAV_CONFIG_QUERY_KEY, firmId] });
        // Per-row NAV-day chips and Apply eligibility on the companies table.
        await queryClient.invalidateQueries({ queryKey: [FIRM_DAILY_NAV_COMPANY_CONFIGS_QUERY_KEY, firmId] });
        await queryClient.invalidateQueries({
          queryKey: companyId
            ? [COMPANY_DAILY_NAV_CONFIG_QUERY_KEY, firmId, companyId]
            : [COMPANY_DAILY_NAV_CONFIG_QUERY_KEY],
        });
        return response;
      } catch (error) {
        processErrorResponse({
          error,
          defaultErrorMessage: 'An error occurred while applying the Daily NAV configuration',
          action: 'apply the Daily NAV configuration',
        });
        return null;
      }
    },
    [mutateAsync, queryClient, processErrorResponse]
  );

  return { applyDailyNavConfig, isPending };
};
